import React from 'react';
import { Link } from 'react-router-dom';
import { FiHeart, FiCamera, FiShoppingBag, FiStar } from 'react-icons/fi';
import PageTransition from '../components/PageTransition';
import SafeIcon from '../common/SafeIcon';
import LogoIcon from '../common/LogoIcon';
import ReviewsCarousel from '../components/ReviewsCarousel';
import FAQSection from '../components/FAQSection';

const Home = () => {
  const images = {
    hero: "https://media-manager-c.questera.ai/greta-media/51a91a93706672e53ae444a23254479bc2f5b1a8165fab41d806a43d2b869fef8268d071e9340f739bfeb737c7b371ae/images/aW1hZ2UvanBlZw==/ed97c67b1eaae9a55591fe6f74b16510.jpeg",
    bed: "https://media-manager-c.questera.ai/greta-media/51a91a93706672e53ae444a23254479bc2f5b1a8165fab41d806a43d2b869fef8268d071e9340f739bfeb737c7b371ae/images/aW1hZ2UvanBlZw==/79a3fba6fee332af15b17853be4cc42e.jpeg",
    local: "https://media-manager-c.questera.ai/greta-media/51a91a93706672e53ae444a23254479bc2f5b1a8165fab41d806a43d2b869fef8268d071e9340f739bfeb737c7b371ae/images/aW1hZ2UvcG5n/6c32acdbb9e7ed54036e6c243066b494.png"
  };

  return (
    <PageTransition>
      {/* Hero Section - Starts from top of screen */}
      <section className="relative h-screen flex items-center justify-center overflow-hidden">
        <div className="absolute inset-0 bg-brand-dark/40 z-10 mix-blend-multiply"></div>
        <div 
          className="absolute inset-0 bg-cover bg-center bg-no-repeat transition-transform duration-[10s] hover:scale-110"
          style={{ backgroundImage: `url(${images.hero})` }}
        ></div>
        
        <div className="relative z-20 text-center px-4 max-w-4xl mx-auto flex flex-col items-center mt-20">
          <span className="font-script text-4xl md:text-5xl text-brand-cream mb-4 block drop-shadow-md">Welcome to</span>
          <h1 className="font-serif text-5xl md:text-8xl text-white font-bold tracking-widest mb-6 drop-shadow-lg uppercase">
            R. Venue
          </h1>
          <p className="text-lg md:text-2xl text-brand-cream font-light mb-10 tracking-wide drop-shadow-md pb-4 border-b border-brand-cream/30">
            Your relaxing modern retreat in Lucban, Quezon.
          </p>
          <Link 
            to="/rates" 
            className="bg-brand-olive hover:bg-brand-dark text-brand-cream px-12 py-4 rounded-sm text-sm uppercase tracking-[0.3em] transition-all duration-300 shadow-xl"
          >
            Reserve Your Stay
          </Link>
        </div>
      </section>

      {/* Intro Section */}
      <section className="py-24 bg-brand-cream text-center px-4">
        <div className="max-w-3xl mx-auto">
          <h2 className="font-serif text-3xl md:text-4xl text-brand-olive mb-6 uppercase tracking-wider">A Peaceful Escape</h2>
          <p className="text-brand-sage leading-relaxed font-light text-lg">
            Experience the perfect blend of comfort and style at R. VENUE Apartelle. Nestled in the vibrant yet serene town of Lucban, our spaces are designed to provide a welcoming, nature-inspired atmosphere.
          </p>
          <div className="mt-14 flex flex-col items-center">
            <LogoIcon className="w-16 h-16 mb-4 shadow-sm" />
            <div className="flex flex-col items-center">
              <span className="font-serif text-2xl md:text-3xl font-bold tracking-[0.2em] text-brand-olive uppercase leading-none">R. Venue</span>
              <span className="font-script text-3xl md:text-4xl text-brand-sage mt-1 leading-none">Apartelle</span>
            </div>
          </div>
        </div>
      </section>

      {/* Heart of the Town Section */}
      <section className="py-24 bg-white border-b border-brand-light/10">
        <div className="container mx-auto px-6 max-w-7xl">
          <div className="grid md:grid-cols-2 gap-16 items-center">
            <div className="order-2 md:order-1 relative">
              <div className="absolute -inset-4 border border-brand-olive/10 rounded-sm translate-x-2 translate-y-2"></div>
              <img 
                src={images.local} 
                alt="Heart of Lucban" 
                className="relative z-10 w-full h-auto rounded-sm shadow-xl object-cover aspect-[4/3] transition-transform duration-700 hover:scale-[1.02]"
              />
            </div>
            <div className="order-1 md:order-2">
              <span className="font-script text-3xl text-brand-sage mb-2 block">Perfectly Situated</span>
              <h2 className="font-serif text-3xl md:text-5xl text-brand-dark mb-6 uppercase tracking-wide leading-tight">Heart of the Town</h2>
              <p className="text-brand-sage font-light leading-relaxed mb-4 text-lg">
                R. Venue Apartelle is ideally situated in the absolute heart of Lucban, where the town's vibrant energy meets convenience. Our prime location means you are just steps away from everything that makes this town special.
              </p>
              <p className="text-brand-sage font-light leading-relaxed mb-6">
                Take a short, pleasant walk to the historic <b>San Luis Obispo Parish Church</b> or enjoy a peaceful morning at the <b>Town Plaza</b>. Whether you're looking for authentic local <b>restaurants</b>, cozy cafes, or <b>specialty stores</b> for your Lucban favorites, everything is within easy reach.
              </p>
              <div className="bg-brand-cream/50 p-6 rounded-sm border-l-4 border-brand-olive italic text-brand-sage text-sm shadow-sm">
                "Experience the luxury of accessibility. At R. Venue, the best of Lucban is right at your doorstep."
              </div>
            </div>
          </div>
        </div>
      </section>

      {/* Lucban Experience Section */}
      <section className="py-24 bg-brand-cream/30 overflow-hidden">
        <div className="container mx-auto px-6 max-w-5xl">
          <div className="text-center mb-16">
            <span className="font-script text-3xl text-brand-sage mb-2 block">The Local Charm</span>
            <h2 className="font-serif text-3xl md:text-5xl text-brand-olive mb-6 leading-tight">Experience the Soul <br/>of Lucban</h2>
            <p className="text-brand-sage font-light leading-relaxed text-lg max-w-3xl mx-auto">
              Lucban is more than a destination — it is a place filled with heritage, celebration, and heartfelt hospitality. Known for its vibrant Pahiyas Festival, beloved delicacies, and picturesque town charm, Lucban invites every guest to experience a stay surrounded by culture and warmth.
            </p>
          </div>
          
          <div className="grid grid-cols-1 md:grid-cols-2 gap-x-12 gap-y-12">
            <div className="flex gap-4">
              <div className="shrink-0 pt-1 text-brand-olive"><SafeIcon icon={FiStar} className="text-2xl" /></div>
              <div>
                <h4 className="font-bold text-brand-dark uppercase tracking-widest text-xs mb-2">Pahiyas in Full Color</h4>
                <p className="text-brand-sage text-sm font-light leading-relaxed">Celebrate the town’s artistic spirit through the world-famous Pahiyas Festival, where streets come alive with creativity.</p>
              </div>
            </div>
            <div className="flex gap-4">
              <div className="shrink-0 pt-1 text-brand-olive"><SafeIcon icon={FiShoppingBag} className="text-2xl" /></div>
              <div>
                <h4 className="font-bold text-brand-dark uppercase tracking-widest text-xs mb-2">A Taste of Lucban</h4>
                <p className="text-brand-sage text-sm font-light leading-relaxed">Enjoy authentic local specialties like Lucban longganisa and pancit habhab, part of the town’s rich culinary identity.</p>
              </div>
            </div>
            <div className="flex gap-4">
              <div className="shrink-0 pt-1 text-brand-olive"><SafeIcon icon={FiHeart} className="text-2xl" /></div>
              <div>
                <h4 className="font-bold text-brand-dark uppercase tracking-widest text-xs mb-2">A Warm Welcome Always</h4>
                <p className="text-brand-sage text-sm font-light leading-relaxed">One of Lucban’s greatest treasures is its people — warm, gracious, and always ready to make visitors feel at home.</p>
              </div>
            </div>
            <div className="flex gap-4">
              <div className="shrink-0 pt-1 text-brand-olive"><SafeIcon icon={FiCamera} className="text-2xl" /></div>
              <div>
                <h4 className="font-bold text-brand-dark uppercase tracking-widest text-xs mb-2">Beauty to Explore</h4>
                <p className="text-brand-sage text-sm font-light leading-relaxed">From historic churches to scenic views, Lucban offers memorable sights that blend faith, culture, and natural charm.</p>
              </div>
            </div>
          </div>
        </div>
      </section>

      {/* Featured Image Split */}
      <section className="flex flex-col md:flex-row bg-brand-olive">
        <div 
          className="md:w-1/2 min-h-[60vh] bg-cover bg-center"
          style={{ backgroundImage: `url(${images.bed})` }}
        ></div>
        <div className="md:w-1/2 flex items-center justify-center p-12 md:p-24 text-brand-cream">
          <div className="max-w-md text-center md:text-left">
            <span className="font-script text-3xl text-brand-light mb-2 block">Rest & Relax</span>
            <h2 className="font-serif text-3xl md:text-4xl mb-6 uppercase tracking-wider">Your Cozy Sanctuary</h2>
            <p className="font-light text-brand-light mb-8 leading-relaxed">
              Wake up every morning with the thought that something wonderful is going to happen. Our rooms are crafted with soft hues and elegant furnishings.
            </p>
            <Link 
              to="/stay" 
              className="inline-block border border-brand-cream text-brand-cream px-10 py-4 uppercase tracking-widest text-sm hover:bg-brand-cream hover:text-brand-olive transition-all duration-300"
            >
              Explore Amenities
            </Link>
          </div>
        </div>
      </section>

      {/* Testimonials Section */}
      <section className="py-24 bg-brand-cream">
        <div className="container mx-auto px-6 max-w-7xl text-center">
          <span className="font-script text-3xl text-brand-sage mb-2 block">Guest Stories</span>
          <h2 className="font-serif text-3xl md:text-4xl text-brand-olive mb-16 uppercase tracking-wider">What Our Visitors Say</h2>
          <ReviewsCarousel />
        </div>
      </section>

      {/* FAQ Section */}
      <FAQSection />

      {/* Quick CTA */}
      <section className="py-20 bg-brand-dark text-brand-cream text-center">
        <div className="max-w-2xl mx-auto px-6">
          <h2 className="font-serif text-3xl mb-8 leading-tight">Ready to experience the beauty of Lucban?</h2>
          <Link 
            to="/rates" 
            className="inline-block bg-brand-cream text-brand-olive px-12 py-4 rounded-sm text-sm uppercase tracking-[0.3em] font-bold hover:bg-brand-olive hover:text-brand-cream transition-all duration-300"
          >
            Check Availability
          </Link>
        </div>
      </section>
    </PageTransition>
  );
};

export default Home;